import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, CanActivateFn, CanActivateChildFn } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { filter, timeout } from 'rxjs/operators';
import { AccountState } from 'src/app/store/account/account.state';
import { GovernanceState } from 'src/app/store/governance/governance.state';

/**
 * Waits for a boolean signal-observable to become true. The observable must
 * be created with toObservable() SYNCHRONOUSLY in the guard's injection
 * context — after the first `await` the context is gone (NG0203).
 */
async function waitForTrue(source$: ReturnType<typeof toObservable<boolean>>): Promise<void> {
  await firstValueFrom(
    source$.pipe(
      filter(value => value === true),
      timeout(10000),
    ),
  );
}

/** Only same-app absolute paths may be preserved as a return destination. */
function safeReturnParams(url: string | undefined): Record<string, string> {
  return url && url.startsWith('/') && !url.startsWith('//') ? { returnUrl: url } : {};
}

/**
 * V3 Governance Guard (D1.5)
 *
 * Blocks V3 surfaces (notebook, admin) while the ACTIVE account is in
 * enforced Compatibility Mode — its governance record resolves
 * PENDING/DECLINED/unknown, or the listener errored (fail closed). The user
 * is redirected to `/my-blume`, where the Lead Administrator sees the
 * decision screen and everyone else sees the account status banner; the
 * attempted internal destination is preserved as `returnUrl` and re-runs
 * every guard when navigated back to after an Accept.
 *
 * SELF-SUFFICIENT: the guard starts the governance listener itself when it
 * is not already running for the active account (deep links can arrive
 * before StateManager initialization). Users with NO active account pass
 * through — profile/license guards own that case. Accounts OUTSIDE the
 * governance program (no record — not yet backfilled) and FULL_V3 accounts
 * pass through unchanged. `/my-blume` itself is intentionally NOT protected
 * so the decision/status surface stays reachable. Deliberately separate
 * from the legal dataOptInGuard.
 */
export const v3GovernanceGuard: CanActivateFn = async (_route, state) => {
  const accountState = inject(AccountState);
  const governanceState = inject(GovernanceState);
  const router = inject(Router);
  // Both observables must be created before the first await (injection context).
  const accountInitialized$ = toObservable(accountState.initialized);
  const governanceLoaded$ = toObservable(governanceState.loaded);

  try {
    const disposition = await resolveActiveAccountGovernance(
      accountState,
      governanceState,
      accountInitialized$,
      governanceLoaded$,
    );
    if (disposition === 'compat') {
      router.navigate(['/my-blume'], { queryParams: safeReturnParams(state.url) });
      return false;
    }
    return true;
  } catch (error) {
    console.error('[V3GovernanceGuard] governance state did not resolve — failing closed:', error);
    router.navigate(['/my-blume'], { queryParams: {} });
    return false;
  }
};

/**
 * V3 Governance RESOLVE guard (86e2dfyuy / 86e2dfp5m).
 *
 * The in-shell variant: guarantees governance is RESOLVED before the routed
 * surface renders (so `GovernanceState.can()` answers are correct on first
 * paint) but does NOT bounce compatibility-mode users — they enter the
 * reduced notebook shell, where capability gating (menus, CTAs, child
 * routes) and the Firestore rules own enforcement. Same self-sufficiency
 * and fail-closed timeout as {@link v3GovernanceGuard}.
 */
export const v3GovernanceResolveGuard: CanActivateFn = async () => {
  const accountState = inject(AccountState);
  const governanceState = inject(GovernanceState);
  const router = inject(Router);
  const accountInitialized$ = toObservable(accountState.initialized);
  const governanceLoaded$ = toObservable(governanceState.loaded);

  try {
    await resolveActiveAccountGovernance(accountState, governanceState, accountInitialized$, governanceLoaded$);
    return true;
  } catch (error) {
    console.error('[V3GovernanceResolveGuard] governance state did not resolve — failing closed:', error);
    router.navigate(['/my-blume'], { queryParams: {} });
    return false;
  }
};

/**
 * Route-data key marking a notebook child's compat disposition:
 * - 'blocked' — a write-only surface (finalize, edit, add, AI) compat must not reach
 * - 'hidden'  — a section removed from the compat shell entirely (Caseload, D2)
 * Both redirect into the compat landing section; unmarked children are open.
 */
export const V3_COMPAT_CHILD_ACCESS = 'v3CompatAccess';

/**
 * Where compat-mode users land when a child route is not available to them:
 * the export-only Observations (Data Collection) form — the compat default
 * screen (there is no compat home page). Exported so the form pages'
 * back-navigation stays in lockstep with the guard's redirect target.
 */
export const COMPAT_LANDING = '/notebook/collect/goal';

/**
 * Route-data key a SHELL route (the `canActivateChild` owner) may set to name
 * where ITS compat users land when a child is unavailable, as a path template
 * with `:param` placeholders resolved from the shell's own route params —
 * e.g. `'/admin/:partnerId/users'` on the admin shell. Shells without it keep
 * the notebook default {@link COMPAT_LANDING}.
 */
export const V3_COMPAT_SHELL_LANDING = 'v3CompatShellLanding';

/**
 * Resolves the compat landing for the shell that owns `childRoute`: the
 * nearest ancestor's {@link V3_COMPAT_SHELL_LANDING} template with `:param`
 * placeholders filled from that ancestor's params, or {@link COMPAT_LANDING}.
 */
function compatLandingFor(childRoute: ActivatedRouteSnapshot): string {
  const shell = childRoute.pathFromRoot?.find(snapshot => snapshot.data?.[V3_COMPAT_SHELL_LANDING]);
  const template = shell?.data[V3_COMPAT_SHELL_LANDING];
  if (typeof template !== 'string') {
    return COMPAT_LANDING;
  }
  return template.replace(/:(\w+)/g, (_match, param: string) => shell?.params[param] ?? '');
}

/**
 * Capability-aware child gate for the notebook shell (86e2dfyuy). Parent
 * `canActivate` guards do NOT re-run on child-to-child navigation, so this
 * is the only enforcement point for write-only children once a compat user
 * is inside the shell. Self-sufficient like the parent guards; fails closed
 * to /my-blume when governance cannot be resolved.
 */
export const v3CompatChildGuard: CanActivateChildFn = async childRoute => {
  const accountState = inject(AccountState);
  const governanceState = inject(GovernanceState);
  const router = inject(Router);
  const accountInitialized$ = toObservable(accountState.initialized);
  const governanceLoaded$ = toObservable(governanceState.loaded);

  try {
    const disposition = await resolveActiveAccountGovernance(
      accountState,
      governanceState,
      accountInitialized$,
      governanceLoaded$,
    );
    if (disposition !== 'compat') {
      return true;
    }
    const access = childRoute.data?.[V3_COMPAT_CHILD_ACCESS] as 'blocked' | 'hidden' | undefined;
    return access ? router.createUrlTree([compatLandingFor(childRoute)]) : true;
  } catch (error) {
    console.error('[V3CompatChildGuard] governance state did not resolve — failing closed:', error);
    return router.createUrlTree(['/my-blume']);
  }
};

/**
 * Shared resolution for every governance guard: waits for account
 * initialization, starts the governance listener when it is not already
 * running for the ACTIVE account (deep links arrive before StateManager
 * init), and waits for the record to load. Throws on timeout — callers own
 * their fail-closed response. Observables are created by the caller inside
 * the guard's injection context (NG0203).
 */
async function resolveActiveAccountGovernance(
  accountState: AccountState,
  governanceState: GovernanceState,
  accountInitialized$: ReturnType<typeof toObservable<boolean>>,
  governanceLoaded$: ReturnType<typeof toObservable<boolean>>,
): Promise<'pass-through' | 'full-v3' | 'compat'> {
  await waitForTrue(accountInitialized$);

  const activeTeamId = accountState.activeTeam()?.id ?? null;
  const email = accountState.accountEmail();
  if (!activeTeamId || !email) {
    return 'pass-through';
  }

  if (governanceState.accountId() !== activeTeamId) {
    governanceState.loadForAccount(activeTeamId, email);
  }
  await waitForTrue(governanceLoaded$);

  return governanceState.compatibilityModeActive() ? 'compat' : 'full-v3';
}
